import { useState } from 'react';
import SidebarModal from '@/components/Modals/Sidebar';
import { SidebarLogoHeader } from '@/components/Modals/Shared/SidebarLogoHeader';
import { CardPlansAll } from '@/components/Modals/SignUp/components/CardPlansAll';
import { useUpsellModal } from '@/hooks/billing/useUpsellModal';
import { getPlanHierarchyLevel } from '@/lib/plan-hierarchy';
import { Button, Text } from '@music-vine/cadence/ui';
import type { ModalConversionPoints } from '@/components/Modals/shared-types';
import type { Plan } from '@/fixtures/plans';
type UpgradeTarget = 'Creator' | 'Pro' | 'Business';
interface UpgradeChoiceModalProps {
  conversionPoint: ModalConversionPoints;
  /** Lowest tier that unlocks the feature being upsold. */
  minimumPlan: UpgradeTarget;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  title: string;
}

/**
 * Plan chooser for paying subscribers upgrading to unlock a feature (WAV,
 * 4K, …). Renders the shared master plan cards filtered to the tiers that
 * actually unlock the feature, and routes the chosen plan through the in-app
 * upgrade flow — never Stripe checkout (DEV-6410 / DEV-6411).
 */
const UpgradeChoiceModal = ({
  open,
  onOpenChange,
  minimumPlan,
  title,
  conversionPoint
}: UpgradeChoiceModalProps) => {
  const minimumLevel = getPlanHierarchyLevel(minimumPlan);

  // Pro is the plan the designs highlight, so it opens by default wherever
  // the feature allows it. Where it doesn't (a Business-only unlock) the
  // floor wins, or the default would select a card that isn't rendered
  // (DEV-6625).
  const [selectedPlan, setSelectedPlan] = useState<Plan['name']>(() => getPlanHierarchyLevel('Pro') >= minimumLevel ? 'Pro' : minimumPlan);
  const {
    openUpsell
  } = useUpsellModal();
  const handleUpgrade = () => {
    onOpenChange(false);
    openUpsell({
      targetPlan: selectedPlan as UpgradeTarget,
      conversionPoint
    });
  };
  return <SidebarModal onOpenChange={onOpenChange} open={open} data-sentry-element="SidebarModal" data-sentry-component="UpgradeChoiceModal" data-sentry-source-file="index.tsx">
      <div className="flex h-full flex-col overflow-y-auto">
        <SidebarLogoHeader onClose={() => onOpenChange(false)} data-sentry-element="SidebarLogoHeader" data-sentry-source-file="index.tsx" />
        <div className="flex flex-1 flex-col gap-6 p-6 pt-10">
          <Text asChild className="text-pretty font-heading font-semibold text-gray-950 text-lg dark:text-white" data-sentry-element="Text" data-sentry-source-file="index.tsx">
            <h2>{title}</h2>
          </Text>
          <CardPlansAll filter={plan => getPlanHierarchyLevel(plan.id) >= minimumLevel} onSelectedPlanChange={setSelectedPlan} selectedPlan={selectedPlan} data-sentry-element="CardPlansAll" data-sentry-source-file="index.tsx" />
          <Button className="mt-auto w-full" onClick={handleUpgrade} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="index.tsx">
            Upgrade
          </Button>
        </div>
      </div>
    </SidebarModal>;
};
export default UpgradeChoiceModal;